/**
 * Navigation Configuration
 *
 * Defines all sections and views for the application navigation.
 * This is the source of truth for the navigation structure.
 *
 * Views can have:
 * - featureFlag: string - Feature flag key that must be enabled to show view
 * - placeholder: boolean - If true, shows "coming soon" placeholder
 * - roles: string[] - Roles that can access this view (admin, pm)
 * - comingSoon: boolean - Shows "(soon)" label but still visible
 *
 * @see docs/architecture/2026-01-08/NAVIGATION-RESTRUCTURE-MASTER-PLAN.md
 */

import { isFeatureEnabled } from './features';

/**
 * Placeholder descriptions for coming-soon features
 */
export const PLACEHOLDER_DESCRIPTIONS = {
  capacity: 'View team workload and capacity planning across time horizons.',
  backlog: 'Manage unscheduled work and prioritize what\'s next.',
  risks: 'Early warning system for delivery risks and blocked items.',
  changes: 'Audit trail for schedule-impacting decisions.',
  exports: 'Download reports and data in various formats.',
  'paint-view': 'Paint queue - manage paint finishing work.',
  'model-view': 'Model queue - manage 3D model production.',
  'shipping-view': 'Shipping queue - coordinate packaging and shipments.',
};

/**
 * Section and view definitions
 *
 * PRODUCTION PUSH VISIBILITY:
 * - Production: Time Tracker, My Work, Team Status, Printers (visible to all)
 * - Production: PA View, Assembly View, Paint & Model View, Shipping View (beta only)
 * - Scheduling: Project Tracker (visible to all), others (beta only)
 * - Reporting: Time Ticket Data, Rework QA, Data Quality (visible to all)
 * - Admin: unchanged (admin only)
 */
export const SECTIONS = {
  'time-tracking': {
    id: 'time-tracking',
    label: 'Track Time',
    icon: 'clock',
    defaultView: 'time-tracker',
    order: 0,
    views: [
      { id: 'time-tracker', label: 'Track Time', icon: 'clock' },
      { id: 'team-printers', label: 'Team & Printers', icon: 'users' },
      // Hidden views - accessible via URL but not shown in nav
      { id: 'history', label: 'Session History', icon: 'history', hidden: true },
    ],
  },
  production: {
    id: 'production',
    label: 'Production',
    icon: 'factory',
    defaultView: 'unified-projects',
    order: 1,
    views: [
      // Primary production views
      { id: 'unified-projects', label: 'All Projects', icon: 'layers' },
      // Master Pipeline - all projects across stages
      { id: 'master-pipeline', label: 'Master Pipeline', icon: 'git-branch', featureFlag: 'PM_COMMAND_CENTER' },
      // Department Pipeline views
      { id: 'pa-pipeline', label: 'PA Pipeline', icon: 'palette', featureFlag: 'PA_QUEUE' },
      { id: 'assembly-pipeline', label: 'Assembly Pipeline', icon: 'wrench', featureFlag: 'ASSEMBLY_QUEUE' },
      { id: 'shipping-pipeline', label: 'Shipping Pipeline', icon: 'truck', featureFlag: 'PM_COMMAND_CENTER', comingSoon: true },
      { id: 'billing-pipeline', label: 'Billing Pipeline', icon: 'dollar-sign', featureFlag: 'PM_COMMAND_CENTER', comingSoon: true },
      // Legacy work center views (hidden, replaced by pipelines)
      { id: 'pa-view', label: 'PA View', icon: 'palette', featureFlag: 'PA_QUEUE', hidden: true },
      { id: 'assembly-view', label: 'Assembly View', icon: 'wrench', featureFlag: 'ASSEMBLY_QUEUE', hidden: true },
      // Coming soon placeholders
      { id: 'paint-view', label: 'Paint', icon: 'paintbrush', placeholder: true },
      { id: 'model-view', label: 'Model', icon: 'cube', placeholder: true },
      // PM Interface (new) - visible to beta users
      { id: 'pm-interface', label: 'PM Interface', icon: 'briefcase', featureFlag: 'PM_COMMAND_CENTER' },
      // PM Command Center (legacy) - hidden but still accessible
      { id: 'pm-center', label: 'PM Center (Legacy)', icon: 'briefcase', featureFlag: 'PM_COMMAND_CENTER', hidden: true },
      // Hidden views - accessible via URL/tabs but not shown in main nav
      { id: 'team-printers', label: 'Team & Printers', icon: 'users', hidden: true }, // Moved to Track Time section
      { id: 'team-status', label: 'Team Status', icon: 'activity', hidden: true },
      { id: 'printers', label: 'Printers', icon: 'printer', hidden: true },
      { id: 'project-detail', label: 'Project Detail', icon: 'file', hidden: true },
      { id: 'project-page', label: 'Project Page', icon: 'file-text', hidden: true }, // Unified project page
      { id: 'my-notifications', label: 'My Notifications', icon: 'bell', hidden: true },
      { id: 'time-tracker', label: 'Track Time', icon: 'clock', hidden: true }, // Legacy route support
    ],
  },
  scheduling: {
    id: 'scheduling',
    label: 'Scheduling',
    icon: 'calendar',
    defaultView: 'project-tracker',
    order: 2,
    views: [
      { id: 'project-tracker', label: 'Project Tracker', icon: 'folder' },
      { id: 'unified-board', label: 'All Projects Board', icon: 'columns' },
      { id: 'capacity', label: 'Capacity', icon: 'bar-chart', placeholder: true },
      // Hidden views - accessible via URL but not shown in nav
      { id: 'board', label: 'Board', icon: 'layout', hidden: true },
      { id: 'backlog', label: 'Backlog', icon: 'list', hidden: true },
      { id: 'risks', label: 'Risks', icon: 'alert-triangle', hidden: true },
      { id: 'changes', label: 'Changes', icon: 'git-commit', hidden: true },
    ],
  },
  sales: {
    id: 'sales',
    label: 'Sales',
    icon: 'dollar-sign',
    defaultView: 'revenue-dashboard',
    order: 3,
    hidden: true, // Temporarily hidden - deployment mistake
    views: [
      { id: 'revenue-dashboard', label: 'Revenue Dashboard', icon: 'trending-up' },
      { id: 'pending-quotes', label: 'Pending Quotes', icon: 'clipboard-list', featureFlag: 'PENDING_QUOTES_PAGE' },
      { id: 'sales-reps', label: 'Sales Rep Pipeline', icon: 'users' },
      { id: 'data-definitions', label: 'Data Definitions', icon: 'book', featureFlag: 'DEFINITIONS_PAGE' },
      { id: 'sales-help', label: 'How-To Guide', icon: 'help-circle', featureFlag: 'HOWTO_GUIDE' },
    ],
  },
  reporting: {
    id: 'reporting',
    label: 'Reporting',
    icon: 'bar-chart-2',
    defaultView: 'time-ticket-data',
    order: 4,
    views: [
      { id: 'time-ticket-data', label: 'Time Ticket Data', icon: 'file-text' },
      { id: 'ticket-history', label: 'Ticket History', icon: 'search' },
      { id: 'rework', label: 'Rework QA', icon: 'alert-triangle' },
      { id: 'data-quality', label: 'Data Quality', icon: 'check-circle' },
      { id: 'more-reports', label: 'More Reports coming soon', icon: 'clock', placeholder: true },
    ],
  },
  administration: {
    id: 'administration',
    label: 'Admin',
    icon: 'settings',
    defaultView: 'users',
    order: 5,
    roles: ['admin'],
    views: [
      { id: 'users', label: 'Users', icon: 'users' },
      { id: 'tickets', label: 'Tickets', icon: 'ticket' },
      { id: 'feedback', label: 'Feedback', icon: 'message-square' },
      { id: 'notifications', label: 'Notifications', icon: 'bell' },
      { id: 'system-health', label: 'System Health', icon: 'activity' },
      { id: 'work-instructions', label: 'Work Instructions', icon: 'book' },
      { id: 'design-system', label: 'Design System', icon: 'paintbrush' },
    ],
  },
  account: {
    id: 'account',
    label: null, // Rendered as avatar
    icon: 'user',
    defaultView: 'profile',
    order: 6,
    renderAs: 'dropdown',
    views: [
      { id: 'profile', label: 'Profile', icon: 'user' },
      { id: 'downloads', label: 'Downloads', icon: 'download' },
      { id: 'send-feedback', label: 'Send Feedback', icon: 'message-circle' },
      { id: 'switch-account', label: 'Switch Account', icon: 'repeat', action: 'switchAccount' },
      { id: 'sign-out', label: 'Sign Out', icon: 'log-out', action: 'signOut' },
    ],
  },
};

/**
 * Get ordered array of sections
 * @returns {Array} Sections sorted by order
 */
export function getOrderedSections() {
  return Object.values(SECTIONS).sort((a, b) => a.order - b.order);
}

/**
 * Get default section for user
 * Landing page is Track Time
 * @param {object} user - User object (unused for now, but available for future role-based defaults)
 * @returns {string} Default section ID
 */
export function getDefaultSection(user) {
  return 'time-tracking';
}

/**
 * Get default view for a section
 * @param {string} sectionId - Section ID
 * @param {object} user - User object
 * @returns {string} Default view ID
 */
export function getDefaultView(sectionId, user) {
  const section = SECTIONS[sectionId];
  if (!section) return null;
  return section.defaultView;
}

/**
 * Check if user can access a section
 * @param {object} sectionConfig - Section configuration
 * @param {object} user - User object
 * @returns {boolean} Whether user can access section
 */
export function isSectionVisible(sectionConfig, user) {
  if (!sectionConfig) return false;
  if (!sectionConfig.roles) return true;

  return sectionConfig.roles.some(role => {
    if (role === 'admin') return user?.isAdmin;
    if (role === 'pm') return user?.isPM;
    return false;
  });
}

/**
 * Check if user can access a view
 * @param {object} viewConfig - View configuration
 * @param {object} user - User object
 * @returns {boolean} Whether user can access view
 */
export function isViewVisible(viewConfig, user) {
  if (!viewConfig) return false;

  // Check role-based access
  if (viewConfig.roles) {
    const hasRole = viewConfig.roles.some(role => {
      if (role === 'admin') return user?.isAdmin;
      if (role === 'pm') return user?.isPM;
      return false;
    });
    if (!hasRole) return false;
  }

  // Check feature flag
  if (viewConfig.featureFlag) {
    const userEmail = user?.email || user?.username;
    if (!isFeatureEnabled(viewConfig.featureFlag, userEmail)) {
      return false;
    }
  }

  return true;
}

/**
 * Validate and correct navigation state
 * Ensures user can access the requested section/view, redirects if not
 * @param {string} section - Requested section ID
 * @param {string} view - Requested view ID
 * @param {object} user - User object
 * @returns {{ section: string, view: string, corrected: boolean, reason?: string }}
 */
export function validateAndCorrectNavState(section, view, user) {
  const sectionConfig = SECTIONS[section];

  // Section doesn't exist or user can't access
  if (!sectionConfig || !isSectionVisible(sectionConfig, user)) {
    const defaultSection = getDefaultSection(user);
    return {
      section: defaultSection,
      view: getDefaultView(defaultSection, user),
      corrected: true,
      reason: 'section_not_accessible',
    };
  }

  // View doesn't exist in section
  const viewConfig = sectionConfig.views.find(v => v.id === view);
  if (!viewConfig) {
    return {
      section,
      view: sectionConfig.defaultView,
      corrected: true,
      reason: 'view_not_found',
    };
  }

  // View exists but user can't access
  if (!isViewVisible(viewConfig, user)) {
    return {
      section,
      view: sectionConfig.defaultView,
      corrected: true,
      reason: 'view_not_accessible',
    };
  }

  return { section, view, corrected: false };
}

/**
 * Get visible sections for user
 * @param {object} user - User object
 * @returns {Array} Visible sections sorted by order
 */
export function getVisibleSections(user) {
  return getOrderedSections().filter(section => isSectionVisible(section, user));
}

/**
 * Get visible views for a section (excludes hidden views)
 * @param {string} sectionId - Section ID
 * @param {object} user - User object
 * @returns {Array} Visible views for display in navigation
 */
export function getVisibleViews(sectionId, user) {
  const section = SECTIONS[sectionId];
  if (!section) return [];
  // Filter out hidden views (they're still accessible via URL, just not shown in nav)
  return section.views.filter(view => !view.hidden && isViewVisible(view, user));
}
